/**
 * hooks/useVendorOrderRooms.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The one way a dashboard screen joins a vendor's live order rooms
 * (4 Oct 2026).
 *
 * It emits `joinVendorOrderRooms({ vendorUserId }, ack)` and the server picks
 * the rooms from the login's access (socketHandlers/vendorRoomJoins.js):
 * every whole-business room (vendor:, restaurantVendor:, restaurant:,
 * healthcareVendor:, healthcare:, kitchen:, brandVendor:) for head office, and
 * vendorBranch:<owner>:<location> for each branch of a branch member. The
 * same order events arrive either way, so the screens keep their listeners.
 * Before this, each screen emitted its own legacy joins with the signed-in
 * user's id; a team member named their own id and heard nothing, and a
 * branch member would have heard every branch.
 *
 * It joins on connect and again on every reconnect. `fallback` runs the old
 * joins when the server never answers (a server older than this dashboard);
 * the server gates those too.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef, useState } from "react";

export const JOIN_EVENT = "joinVendorOrderRooms";
const ACK_TIMEOUT_MS = 8000;

/**
 * Join the rooms on `socket` now (when connected) and on every connect.
 *
 * @param {object} socket a socket.io client
 * @param {string} vendorUserId the OWNER's Users id
 * @param {{ onResult?: (ack: {ok, scoped, rooms}) => void,
 *           fallback?: (socket) => void, timeoutMs?: number }} [opts]
 * @returns {() => void} stop: removes the connect handler and any pending fallback
 */
export const attachVendorOrderRooms = (socket, vendorUserId, { onResult, fallback, timeoutMs = ACK_TIMEOUT_MS } = {}) => {
  if (!socket || typeof socket.emit !== "function" || !vendorUserId) return () => {};
  let stopped = false;
  let timer = null;
  const join = () => {
    let answered = false;
    clearTimeout(timer);
    socket.emit(JOIN_EVENT, { vendorUserId: String(vendorUserId) }, (ack) => {
      answered = true;
      clearTimeout(timer);
      if (!stopped && typeof onResult === "function") onResult(ack || { ok: false, scoped: false, rooms: [] });
    });
    if (typeof fallback === "function") {
      timer = setTimeout(() => {
        if (!answered && !stopped) fallback(socket);
      }, timeoutMs);
    }
  };
  // Connected now: join now. Either way the connect handler covers the first
  // connect and every reconnect, so the join is never sent twice.
  if (socket.connected) join();
  if (typeof socket.on === "function") socket.on("connect", join);
  return () => {
    stopped = true;
    clearTimeout(timer);
    if (typeof socket.off === "function") socket.off("connect", join);
  };
};

/**
 * React face of attachVendorOrderRooms. Returns the last answer:
 * { ok, scoped, rooms } (null until the server has answered).
 *
 * @param {object|null} socket
 * @param {string|null} vendorUserId the OWNER's Users id; nothing is joined without one
 * @param {{ enabled?: boolean, fallback?: (socket) => void }} [opts]
 */
export default function useVendorOrderRooms(socket, vendorUserId, { enabled = true, fallback } = {}) {
  const [result, setResult] = useState(null);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  useEffect(() => {
    if (!enabled || !socket || !vendorUserId) return undefined;
    return attachVendorOrderRooms(socket, vendorUserId, {
      onResult: setResult,
      fallback: fallbackRef.current ? (s) => fallbackRef.current?.(s) : undefined,
    });
  }, [socket, vendorUserId, enabled]);
  return result;
}
